import {Box, Typography} from '@mui/material';
import './BatchPrintMain.scss';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrButton from '../common/arr-button/ArrButton';
import {ArrowBack} from '@mui/icons-material';
import {useEffect, useMemo, useState, useRef} from 'react';
import {ArrStepper, Step} from '../common/arr-stepper/ArrStepper';
import {
  batchPrintStepperItems,
  STEP_KEY_ANALYSIS,
  STEP_KEY_EXPORT_FORMAT,
  STEP_KEY_EXCEPTION_FILE_TYPES,
  STEP_KEY_BRANDING,
} from './batchPrintUtils';
import SourceSelection from './source-selection/SourceSelection';
import {useDispatch, useSelector} from 'react-redux';
import {ToastType} from '../../shared/constants';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import BasisForPrinting from './basis-for-printing/BasisForPrinting';
import {
  resetBatchPrintData,
  setAnalysisTranslationAction,
  setAnalysisTranslationSkipOrIgnoreDocuments,
  setSaveBatchPrint,
  setSelectedSource,
  setSelectedSourceActionType,
} from '../../redux/slice/batchPrintSlice';
import SlipSheetAndCoverPage from './slipsheets-and-coverpage/SlipSheetAndCoverPage';
import Analysis from './analysis/Analysis';
import ExportFormat from './export-format/ExportFormat';
import ExceptionFileTypes from './exception-file-types/ExceptionFileTypes';
import BrandingAndRedactions from './branding-and-redactions/BrandingAndRedactions';
import {useBatchPrintSave} from '../../hooks/documentPrinting/useDocumentPrinting';
import {formatDateCommon} from '../../utils/utils';
import {useNavigate} from 'react-router-dom';

const shortInfo = () => (
  <>
    <p data-testid={`batch-print-main-tt0`} tabIndex={0}>
      {t('batchPrint.toolTip.tt0')}
    </p>
    <p data-testid={`batch-print-main-tt1`} tabIndex={0}>
      {t('batchPrint.toolTip.tt1')}
    </p>
    <p data-testid={`batch-print-main-tt2`} tabIndex={0}>
      {t('batchPrint.toolTip.tt2')}
    </p>
    <p data-testid={`batch-print-main-tt3-tt4`} tabIndex={0}>
      {t('batchPrint.toolTip.tt3')}
      <a
        href="https://www.sightlinecentral.com/documentation/batch-print-with-cover-and-intro-page/"
        data-testid={`batch-print-main-tt4`}
        tabIndex={0}
      >
        {t('batchPrint.toolTip.tt4')}
      </a>
    </p>
  </>
);

const BatchPrintMain = () => {
  const [steps, setSteps] = useState<Step[]>(batchPrintStepperItems());
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const slipSheetRef = useRef<any>(null);
  const stepperWrapperRef = useRef<HTMLDivElement | null>(null);
  const currentIndex = steps.findIndex((s) => s.state === 'current');
  const batchPrint = useSelector((state: any) => state?.batchPrint);
  const batchPrintDTO = batchPrint?.saveBatchPrint?.batchPrintDTO;
  const analysis = batchPrint?.analysis;
  const exceptionFileTypes = batchPrint?.exceptionFileTypes;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {mutate: updateBatchPrintSave} = useBatchPrintSave();

  const SKIPPED_INDEX = 3;
  const shouldSkipExceptionStep = useMemo(() => {
    return batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT';
  }, [batchPrintDTO]);

  const BRANDING_AND_REDACTIONS_INDEX = 5;
  const shouldSkipBrandingAndRedactionsNew =
    batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT';

  useEffect(() => {
    return () => {
      dispatch(setSelectedSource(''));
      dispatch(setSelectedSourceActionType(''));
      dispatch(setAnalysisTranslationAction('natives'));
      dispatch(setAnalysisTranslationSkipOrIgnoreDocuments([]));
      dispatch(
        setSaveBatchPrint({
          includeTableOfContent: false,
          displayTOCNumberInSeq: false,
          includeSlipSheet: true,
          isNewSlipSheet: false,
          slipSheetDetails: [],
          includeCoverIntroPage: false,
          coverSheetRecipientName: '',
          coverSheetSenderName: '',
          coverSheetCarbonCopyName: '',
          coverSheetTitle: '',
          coverSheetDescription: '',
          coverSheetDate: '',
          isSlipSheetStepValid: false,
        })
      );
    };
  }, [dispatch]);

  const goNext = () => {
    if (
      steps[currentIndex].key === 'sourceSelection' &&
      batchPrint?.selectedSource?.length == 0
    ) {
      return triggerToast(
        t('batchPrint.sourceSelection.warningMsg'),
        ToastType.Warning
      );
    }
    if (steps[currentIndex].key === 'analysis') {
      let message = '';

      const decisions = analysis?.decisions ?? {};
      const skippedToggle = analysis?.skipFolderToggle;
      const selectedSkippedFolder = analysis?.selectedSkippedFolder;
      const decisionValues = Object.values(decisions);

      const allSkipped =
        decisionValues.length > 0 &&
        decisionValues.every((d) => d.action === 'SKIP');

      if (
        analysis?.printTotalDocCount > 0 &&
        analysis?.printTotalDocCount === analysis?.notAnyProductionDocCount
      ) {
        message = t('batchPrint.analysisGroup.docNotInProduction');
      } else if (
        (analysis?.printTotalDocCount > 0 &&
          analysis?.printTotalDocCount === analysis?.moreProducitonDocCount &&
          allSkipped) ||
        (analysis?.printTotalDocCount === 0 &&
          batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT')
      ) {
        message = t('batchPrint.analysisGroup.selectAnyProduction');
      } else if (
        skippedToggle &&
        selectedSkippedFolder === '' &&
        analysis?.decisionDocCount > 0
      ) {
        message = t('batchPrint.analysisGroup.selectedOneFolder');
      }

      if (message && batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT') {
        return triggerToast(message, ToastType.Warning);
      }
    }
    if (steps[currentIndex].key === STEP_KEY_EXCEPTION_FILE_TYPES) {
      let message = '';
      if (
        exceptionFileTypes?.mediaFilesDocCount > 0 &&
        batchPrintDTO?.includeMediaFilePlaceHolder &&
        !batchPrintDTO?.mediaFilePlaceHolderHtml
      ) {
        message = t('batchPrint.exceptionFileTypesStep.mediaFilesWarning');
      }
      if (
        exceptionFileTypes?.excelFilesDocCount > 0 &&
        batchPrintDTO?.includeExcelFilePlaceHolder &&
        !batchPrintDTO?.excelFilePlaceHolderHtml
      ) {
        message +=
          '\n ' + t('batchPrint.exceptionFileTypesStep.excelFilesWarning');
      }
      if (
        exceptionFileTypes?.otherExceptionFileTypes > 0 &&
        batchPrintDTO?.includeOtherExceptionFileTypesPlaceHolder &&
        !batchPrintDTO?.otherExceptionFileTypesPlaceHolderHtml
      ) {
        message +=
          '\n' + t('batchPrint.exceptionFileTypesStep.otherFilesWarning');
      }
      if (message) {
        return triggerToast(message, ToastType.Warning);
      }
    }
    if (steps[currentIndex].key === 'slipSheets') {
      slipSheetRef.current?.saveData();
    }
    if (currentIndex !== steps.length - 1) {
      let nextIndex = currentIndex + 1;
      const shouldSkip = nextIndex === SKIPPED_INDEX && shouldSkipExceptionStep;
      const shouldSkipBAR =
        nextIndex === BRANDING_AND_REDACTIONS_INDEX &&
        shouldSkipBrandingAndRedactionsNew;

      if (shouldSkip || shouldSkipBAR) {
        nextIndex += 1;
      }

      setSteps((prev) =>
        prev.map((step, i) =>
          i === currentIndex
            ? {...step, state: 'completed'}
            : (shouldSkip && i === SKIPPED_INDEX) ||
                (shouldSkipBAR && i === BRANDING_AND_REDACTIONS_INDEX)
              ? {...step, state: 'completed'}
              : i === nextIndex
                ? {...step, state: 'current'}
                : step
        )
      );
    } else {
      const formatDate = formatDateCommon(
        batchPrintDTO?.coverSheetDate,
        'YYYY-MM-DD[T]00:00:00'
      );

      const skippedToggle = analysis?.skipFolderToggle;
      const printIssueCode =
        batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT'
          ? !skippedToggle ||
            batchPrintDTO?.folderIDForIssueSkippedDocuments === 0
            ? ''
            : 'SKIPPRINTBUTFOLDER'
          : 'IGNOREPRINT';
      const {
        isSlipSheetStepValid: _isSlipSheetStepValid,
        ...batchPrintDTOPayload
      } = batchPrintDTO;

      const payload = {
        batchPrintDTO: {
          ...batchPrintDTOPayload,
          coverSheetDate: formatDate,
          issueDocuments:
            batchPrintDTO?.printIssueSettingCode === 'SKIPPRINTBUTFOLDER'
              ? []
              : batchPrintDTO?.issueDocuments,
          printIssueSettingCode: printIssueCode,
          isProductionPrint:
            batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT',
          folderIDForIssueSkippedDocuments: !skippedToggle
            ? 0
            : batchPrintDTO?.folderIDForIssueSkippedDocuments,
          includeRedactions:
            batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT'
              ? false
              : batchPrintDTO?.includeRedactions,
        },
      };

      updateBatchPrintSave(payload, {
        onSuccess: () => {
          triggerToast(t('batchPrint.generateSuccess'), ToastType.Success);
          setTimeout(() => {
            dispatch(resetBatchPrintData());
            navigate('/Background/BackgroundTask');
          }, 3000);
        },
        onError: (error) => {
          triggerToast(error?.message, ToastType.Error);
        },
      });
    }
  };

  const goPrevious = () => {
    if (steps[currentIndex].key === 'slipSheets') {
      slipSheetRef.current?.saveData();
    }
    let prevIndex = currentIndex - 1;
    const shouldSkip =
      currentIndex - 1 === SKIPPED_INDEX && shouldSkipExceptionStep;
    const shouldSkipBAR =
      currentIndex - 1 === BRANDING_AND_REDACTIONS_INDEX &&
      shouldSkipBrandingAndRedactionsNew;

    if (shouldSkip || shouldSkipBAR) {
      prevIndex -= 1;
    }
    setSteps((prev) =>
      prev.map((step, i) =>
        i === currentIndex
          ? {...step, state: 'future'}
          : (shouldSkip && i === SKIPPED_INDEX) ||
              (shouldSkipBAR && i === BRANDING_AND_REDACTIONS_INDEX)
            ? {...step, state: 'future'}
            : i === prevIndex
              ? {...step, state: 'current'}
              : step
      )
    );
  };

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const isDisabled =
    (currentIndex === 0 && batchPrint?.selectedSource?.length === 0) ||
    (currentIndex === 1 &&
      batchPrintDTO?.printIssueSettingCode === 'IGNOREPRINT' &&
      (!batchPrintDTO?.productionSetID ||
        batchPrintDTO?.productionIDs.length === 0)) ||
    (currentIndex === 2 &&
      (analysis?.translationAction == 'skip' ||
        analysis?.translationAction == 'ignore') &&
      analysis?.translationSkipOrIgnoreDocuments?.length === 0) ||
    (currentIndex === 4 && !batchPrintDTO?.isSlipSheetStepValid);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const currentStepEl =
        stepperWrapperRef.current?.querySelector<HTMLElement>(
          '[aria-current="step"]'
        );

      currentStepEl?.focus();
    });

    return () => window.cancelAnimationFrame(id);
  }, [currentIndex]);

  return (
    <Box className="batch-print-main" ref={stepperWrapperRef}>
      <Box className="coding-form-header">
        <Box className="coding-form-title">
          <Typography
            className="typo-text headline3_size coding-form-title-text"
            data-testid={`batch-print-main-title`}
            tabIndex={0}
          >
            {t('batchPrint.title')}
          </Typography>
          <ArrTooltipWrapper
            title={t('batchPrint.title')}
            closeTooltipOnEscPress={true}
            trigger={
              <InfoIcon
                fontSize="medium"
                className="coding-form-info-icon"
                tabIndex={0}
                aria-label="info-icon"
              />
            }
          >
            {shortInfo()}
          </ArrTooltipWrapper>
        </Box>
        <Box>
          <ArrButton
            aria-label={t('batchPrint.goToBackgroundTasks')}
            startIcon={<ArrowBack />}
            variant="secondary"
            size="small"
            onClick={() => {
              navigate('/Background/BackgroundTask');
            }}
            className="new-coding-form-button"
            dataTestId="go-to-bg-task"
            id="new-coding-form"
            name="new-coding-form"
          >
            {t('batchPrint.goToBackgroundTasks')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="batch-print-stepper">
        <ArrStepper
          dataTestId={'batch-print-stepper'}
          steps={steps}
          currentStepKey={steps[currentIndex]?.key || ''}
          allowNonLinear={false}
          onStepChange={(key) => {
            setSteps((prev) =>
              prev.map((s) =>
                s.key === key
                  ? {...s, state: 'current'}
                  : s.state === 'current'
                    ? {...s, state: 'completed'}
                    : s
              )
            );
          }}
        />
      </Box>
      <Box className="stepper-button">
        <ArrButton
          onClick={goPrevious}
          disabled={currentIndex === 0}
          isAriaDisabled={currentIndex === 0}
          startIcon={<ArrowBack />}
          className={currentIndex === 0 ? 'hide' : ''}
          variant="secondary"
          size="medium"
          dataTestId="batch-print-main-back-button"
        >
          {t('common.back')}
        </ArrButton>
        <ArrButton
          variant="primary"
          size="medium"
          onClick={goNext}
          disabled={isDisabled}
          aria-label={
            currentIndex === steps.length - 1
              ? t('batchPrint.generate')
              : t('common.next')
          }
          isAriaDisabled={isDisabled}
          dataTestId="batch-print-main-button"
        >
          {currentIndex === steps.length - 1
            ? t('batchPrint.generate')
            : t('common.next')}
        </ArrButton>
      </Box>
      <Box className="stepper-content">
        {steps[currentIndex]?.key == 'sourceSelection' ? (
          <SourceSelection />
        ) : steps[currentIndex]?.key == 'basisPrinting' ? (
          <BasisForPrinting />
        ) : steps[currentIndex]?.key == STEP_KEY_ANALYSIS ? (
          <Analysis />
        ) : steps[currentIndex]?.key == STEP_KEY_EXPORT_FORMAT ? (
          <ExportFormat />
        ) : steps[currentIndex]?.key == STEP_KEY_EXCEPTION_FILE_TYPES ? (
          <ExceptionFileTypes />
        ) : steps[currentIndex]?.key == STEP_KEY_BRANDING ? (
          <BrandingAndRedactions />
        ) : steps[currentIndex]?.key == 'slipSheets' ? (
          <SlipSheetAndCoverPage ref={slipSheetRef} />
        ) : (
          'Content for each step will go here.'
        )}
      </Box>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default BatchPrintMain;
